import { faChevronDown, faChevronRight, faRotate } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ActionButton } from '~/team_tools/components/ActionButton'
import { BUILD_READS_REMOTE_CONFIG, NOT_READ_BY_BUILD } from '~/team_tools/components/buildReads'
import { ConfirmBar } from '~/team_tools/components/ConfirmBar'
import { useNotification } from '~/team_tools/components/NotificationProvider'
import { CLIENT, ConfigChange, ConfigKey, FleetRow, deleteKey, getConfig, getHistory, saveKey } from './api'
import { GameWindowEditor, GameWindowValue, asWindows } from './GameWindow'
import { DepositMatchCard } from './DepositMatch'
import {
    JoinLatencyGateForm,
    JoinLatencyValue,
    asLatencyGate,
    latencyGateProblem,
} from './JoinLatencyGate'

/**
 * Remote config, on the website.
 *
 * What the game reads used to live in Unity's dashboard, then in Django
 * admin. Neither is where the people who change it work, and admin in
 * particular asks for a JSON blob in a textarea with no idea what the key is
 * for or who touched it last.
 *
 * The server owns everything that matters: which keys exist, who receives
 * each one, and whether a value is allowed. This screen does not second-guess
 * any of it -- it shows what the registry says, sends what was typed, and
 * prints the registry's own refusal when it comes back. The one thing it adds
 * is the reading: what each key does, when it last changed and by whom, and
 * whether the fleet has actually picked the change up.
 */

const STATE_STYLE: Record<string, string> = {
    applied: 'text-green-400',
    pending: 'text-amber-300',
    behind: 'text-red-400',
    never: 'text-white/30',
}

const ago = (iso: string | null) => {
    if (!iso) return 'never'
    const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000))
    if (s < 60) return `${s}s ago`
    if (s < 3600) return `${Math.round(s / 60)}m ago`
    if (s < 86400) return `${Math.round(s / 3600)}h ago`
    return `${Math.round(s / 86400)}d ago`
}

/** Pretty JSON, and a stable string to compare an edit against. */
const pretty = (value: unknown) => (value === null || value === undefined ? '' : JSON.stringify(value, null, 2))

/** The one key that is not really a setting: it decides whether anybody can
 *  play. It is lifted out of the list and edited in place. */
const GAME_WINDOW_KEY = 'PreseasonGames'

/** The ping cap. A form of its own because it is four named settings, three of
 *  them optional in a way JSON hides: a field left out keeps whatever the game
 *  build compiled, which is not the same as zero and not the same as off. */
const LATENCY_KEY = 'JoinLatencyGate'

export const RemoteConfig = () => {
    const { addNotification } = useNotification()
    const [data, setData] = useState<Awaited<ReturnType<typeof getConfig>> | null>(null)
    const [loading, setLoading] = useState(true)
    const [open, setOpen] = useState<string | null>(null)
    // Everything except the go-live times. Collapsed, because on most visits
    // the times are the whole errand.
    const [showRest, setShowRest] = useState(false)

    const load = useCallback(async () => {
        setLoading(true)
        try {
            setData(await getConfig())
        } catch (err: any) {
            addNotification(err.message, 'error')
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        load()
    }, [load])

    const keys = data?.keys ?? []
    const gameWindow = keys.find((k) => k.key === GAME_WINDOW_KEY) ?? null
    const latencyGate = keys.find((k) => k.key === LATENCY_KEY) ?? null
    const client = keys.filter(
        (k) => k.audience === CLIENT && k.key !== GAME_WINDOW_KEY && k.key !== LATENCY_KEY
    )
    const server = keys.filter((k) => k.audience && k.audience !== CLIENT)
    const orphans = keys.filter((k) => k.orphan)

    const fleet = data?.fleet ?? []
    const applied = fleet.filter((f) => f.state === 'applied').length
    const behind = fleet.filter((f) => f.state === 'behind').length

    const opened = open ? (keys.find((k) => k.key === open) ?? null) : null

    return (
            <div className='flex w-full flex-col gap-4 text-xs font-mono'>
                {!BUILD_READS_REMOTE_CONFIG && (
                    <div className='rounded border border-amber-300/40 px-3 py-2 text-amber-300'>
                        {NOT_READ_BY_BUILD}. These keys are saved and delivered, but players on that build do not get
                        them; editing still works and takes effect when a build reads them.
                    </div>
                )}
                {/* The revision number and the fleet's adoption of it are
                    real, but they are a developer's reading of a counter, not
                    an answer to "when does the game open". They moved down to
                    Other options, beside the keys they are about. */}
                <div className='flex flex-row flex-wrap items-baseline gap-x-4 gap-y-1'>
                    {behind > 0 && (
                        <span className='text-red-400'>
                            {behind} of {fleet.length} servers have not picked up the last change yet
                        </span>
                    )}
                    {/* No zone picker here: there is one at the top of
                        Team Tools and it drives every date box on every
                        screen, so a second one beside this panel was the same
                        control twice. */}
                    <span className='ml-auto flex flex-row items-center gap-3'>
                        <ActionButton onClick={async () => load()} title='Refresh'>
                            <FontAwesomeIcon icon={faRotate} spin={loading} />
                        </ActionButton>
                    </span>
                </div>

                {gameWindow && <GameWindowCard entry={gameWindow} onSaved={load} />}

                {/* Lifted out of the key list, like the go-live times above.
                    Both decide whether somebody can play at all: one by the
                    clock, one by their connection. Leaving the ping cap as a
                    row of JSON among thirty other keys meant nobody could
                    change it without knowing it was called JoinLatencyGate. */}
                {latencyGate && <LatencyCard entry={latencyGate} onSaved={load} />}

                {/* Not a remote config key at all -- its own singleton row and
                    its own endpoint. It sits here because this is the screen
                    for "what is the game doing right now", and how much bonus
                    credit every deposit earns is part of that. */}
                <DepositMatchCard />

                <div className='flex flex-col gap-2'>
                    <button
                        type='button'
                        onClick={() => setShowRest((v) => !v)}
                        className='flex w-full flex-row items-center gap-2 rounded border border-white/10 px-3 py-2 text-[10px] uppercase tracking-widest text-white/50 hover:border-white/30 hover:text-white'
                    >
                        <FontAwesomeIcon icon={showRest ? faChevronDown : faChevronRight} />
                        Other options
                        <span className='ml-auto normal-case tracking-normal text-white/25'>
                            {keys.length - (gameWindow ? 1 : 0) - (latencyGate ? 1 : 0)} more keys the
                            game reads
                        </span>
                    </button>

                    {showRest && (
                        <div className='flex flex-col gap-4 border-l border-white/10 pl-3'>
                            <span className='text-white/30'>
                                On revision {data?.revision ?? '—'}, changed {ago(data?.changed_at ?? null)}
                                {fleet.length > 0 ? ` · ${applied}/${fleet.length} servers on it` : ''}. The game
                                falls back to its compiled value for anything off or unset.
                            </span>
                            <Section
                                title='Read by the game client, before login'
                                hint='Anyone holding the build receives these, signed in or not.'
                                keys={client}
                                onOpen={setOpen}
                            />
                            <Section
                                title='Read by the game servers'
                                hint='Delivered on the roster sync; each server echoes back the revision it applied.'
                                keys={server}
                                onOpen={setOpen}
                            />
                            {orphans.length > 0 && (
                                <Section
                                    title='No build reads these any more'
                                    hint='Stored but never delivered. Safe to delete.'
                                    keys={orphans}
                                    onOpen={setOpen}
                                />
                            )}
                            {fleet.length > 0 && <Fleet rows={fleet} />}
                        </div>
                    )}
                </div>

                {opened && (
                    <KeySheet
                        entry={opened}
                        onClose={() => setOpen(null)}
                        onSaved={() => {
                            setOpen(null)
                            load()
                        }}
                    />
                )}
            </div>
    )
}

/**
 * The go-live times, edited where they are read rather than behind a row
 * called PreseasonGames. Saving goes through the same endpoint as any other
 * key, so the registry still gets the last word on the shape.
 */
const GameWindowCard = ({ entry, onSaved }: { entry: ConfigKey; onSaved: () => void }) => {
    const { addNotification } = useNotification()
    const [form, setForm] = useState<GameWindowValue>(() => asWindows(entry.value))
    const [saving, setSaving] = useState(false)
    // A key nobody has set has no stored value to differ from, and the form
    // still has to show something, so comparing the two called it unsaved
    // before anybody had touched it. Whether it was edited is the real test.
    const [touched, setTouched] = useState(false)
    const original = useMemo(() => pretty(entry.value), [entry.updated_at, entry.exists])
    const dirty = touched && JSON.stringify(form, null, 2) !== original

    // A fresh load (someone else saved, or the refresh button) should win over
    // an untouched form.
    useEffect(() => {
        setForm(asWindows(entry.value))
        setTouched(false)
    }, [entry.updated_at, entry.exists])

    const save = async () => {
        setSaving(true)
        try {
            const res = await saveKey(entry.key, { value: form })
            addNotification(res.unchanged ? 'Nothing changed' : `Saved — now on r${res.revision}`, 'success')
            onSaved()
        } catch (err: any) {
            addNotification(err.message, 'error')
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className='flex flex-col gap-3 rounded border border-white/15 p-3'>
            <div className='flex flex-row flex-wrap items-baseline gap-3'>
                <span className='text-[10px] uppercase tracking-widest text-sky-300'>When the game is open</span>
                {!entry.enabled && entry.exists && (
                    <span className='text-amber-300'>switched off, so the game ignores these times</span>
                )}
                {entry.deliverable_error && <span className='text-red-400'>{entry.deliverable_error}</span>}
                <span className='ml-auto text-white/30'>
                    {entry.updated_by ? `${entry.updated_by} · ${ago(entry.updated_at)}` : 'never set here'}
                </span>
            </div>

            <GameWindowEditor
                value={form}
                onChange={(next) => {
                    setTouched(true)
                    setForm(next)
                }}
                inForce={entry.exists && entry.enabled && !entry.deliverable_error}
            />

            {/* D-055: said on the card because nothing else says it. The game
                client enforces the window, and current builds read it from
                Unity Game Services; the backend does not refuse a paid join
                outside it, so the window is only as strict as the build. */}
            <p className='text-white/50'>
                The game client enforces these times (current builds read them from Unity Game Services). The
                backend does not refuse a paid join outside them.
            </p>

            <div className='flex flex-row items-center gap-3'>
                {dirty && <span className='uppercase tracking-wide text-white/50'>unsaved</span>}
                <button
                    type='button'
                    onClick={save}
                    disabled={!dirty || saving}
                    className='ml-auto rounded bg-violet-600/80 px-4 py-1 text-xs font-bold uppercase tracking-wide hover:bg-violet-600 disabled:opacity-30'
                >
                    {saving ? 'saving…' : 'Save and send'}
                </button>
            </div>
        </div>
    )
}

/** The ping cap, lifted out of the key list for the same reason the go-live
 *  times are: it decides whether somebody can play. Same card shape, so the
 *  two read as one pair of decisions rather than a feature and an exception.
 */
const LatencyCard = ({ entry, onSaved }: { entry: ConfigKey; onSaved: () => void }) => {
    const { addNotification } = useNotification()
    const [form, setForm] = useState<JoinLatencyValue>(() => asLatencyGate(entry.value))
    const [saving, setSaving] = useState(false)
    const [touched, setTouched] = useState(false)
    const original = useMemo(() => pretty(entry.value), [entry.updated_at, entry.exists])
    const dirty = touched && JSON.stringify(form, null, 2) !== original
    const problem = latencyGateProblem(form)

    useEffect(() => {
        setForm(asLatencyGate(entry.value))
        setTouched(false)
    }, [entry.updated_at, entry.exists])

    const save = async () => {
        setSaving(true)
        try {
            const res = await saveKey(entry.key, { value: form })
            addNotification(res.unchanged ? 'Nothing changed' : `Saved — now on r${res.revision}`, 'success')
            onSaved()
        } catch (err: any) {
            addNotification(err.message, 'error')
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className='flex flex-col gap-3 rounded border border-white/15 p-3'>
            <div className='flex flex-row flex-wrap items-baseline gap-3'>
                <span className='text-[10px] uppercase tracking-widest text-sky-300'>
                    Who is allowed to connect
                </span>
                {!entry.enabled && entry.exists && (
                    <span className='text-amber-300'>switched off, so the game uses its own caps</span>
                )}
                {entry.deliverable_error && <span className='text-red-400'>{entry.deliverable_error}</span>}
                <span className='ml-auto text-white/30'>
                    {entry.updated_by ? `${entry.updated_by} · ${ago(entry.updated_at)}` : 'never set here'}
                </span>
            </div>

            <JoinLatencyGateForm
                value={form}
                onChange={(next) => {
                    setTouched(true)
                    setForm(next)
                }}
            />

            <div className='flex flex-row items-center gap-3'>
                {dirty && <span className='uppercase tracking-wide text-white/50'>unsaved</span>}
                <button
                    type='button'
                    onClick={save}
                    disabled={!dirty || saving || Boolean(problem)}
                    className='ml-auto rounded bg-violet-600/80 px-4 py-1 text-xs font-bold uppercase tracking-wide hover:bg-violet-600 disabled:opacity-30'
                >
                    {saving ? 'saving…' : 'Save and send'}
                </button>
            </div>
        </div>
    )
}

const Section = ({
    title,
    hint,
    keys,
    onOpen,
}: {
    title: string
    hint: string
    keys: ConfigKey[]
    onOpen: (key: string) => void
}) => {
    if (keys.length === 0) return null
    return (
        <div className='flex flex-col gap-1'>
            <div className='flex flex-row items-baseline gap-2'>
                <span className='text-[10px] uppercase tracking-widest text-sky-300'>{title}</span>
                <span className='text-[10px] text-white/30'>{hint}</span>
            </div>
            {keys.map((k) => (
                <div
                    key={k.key}
                    role='button'
                    tabIndex={0}
                    onClick={() => onOpen(k.key)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            onOpen(k.key)
                        }
                    }}
                    className={`grid cursor-pointer grid-cols-[14rem,minmax(0,1fr),7rem,8rem] items-center gap-3 rounded border px-3 py-2 hover:border-white/30 ${
                        k.deliverable_error
                            ? 'border-red-400/40'
                            : k.exists && k.enabled
                              ? 'border-white/10'
                              : 'border-white/5'
                    }`}
                >
                    <span className='flex min-w-0 flex-col'>
                        <span className={`truncate font-bold ${k.exists && k.enabled ? '' : 'text-white/40'}`}>
                            {k.key}
                        </span>
                        <span className='truncate text-white/30'>
                            {k.updated_by ? `${k.updated_by} · ${ago(k.updated_at)}` : 'never set here'}
                        </span>
                    </span>
                    <span className='truncate text-white/40'>{k.help}</span>
                    <span
                        className={
                            !k.exists
                                ? 'text-white/25'
                                : k.deliverable_error
                                  ? 'text-red-400'
                                  : k.enabled
                                    ? 'text-green-400'
                                    : 'text-white/40'
                        }
                    >
                        {/* "live" meant "we deliver it", which is not the same
                            as players getting it: the current build reads none
                            of these keys, so it says "saved" until one does. */}
                        {!k.exists
                            ? 'not set'
                            : k.deliverable_error
                              ? 'not deliverable'
                              : k.enabled
                                ? BUILD_READS_REMOTE_CONFIG
                                    ? 'live'
                                    : 'saved'
                                : 'off'}
                    </span>
                    <span className='truncate text-right text-[10px] uppercase tracking-widest text-white/30'>
                        {k.exists ? 'edit' : 'set it'}
                    </span>
                </div>
            ))}
        </div>
    )
}

const Fleet = ({ rows }: { rows: FleetRow[] }) => (
    <div className='flex flex-col gap-1'>
        <span className='text-[10px] uppercase tracking-widest text-white/40'>Which servers have it</span>
        <div className='flex flex-row flex-wrap gap-1.5'>
            {rows.map((r) => (
                <span
                    key={r.id}
                    title={`echoed r${r.echoed ?? '—'}`}
                    className='rounded border border-white/10 px-2 py-0.5'
                >
                    <span className='text-white/60'>{r.name ?? r.slot}</span>{' '}
                    <span className={STATE_STYLE[r.state] ?? 'text-white/30'}>{r.state}</span>
                </span>
            ))}
        </div>
    </div>
)

/**
 * One key: what it is for, its JSON, its history.
 *
 * The JSON is typed as text and parsed on save, so a half-typed brace does
 * not fight the cursor. The server validates it again -- properly, field by
 * field -- and whatever it says comes back onto the screen unchanged.
 */
const KeySheet = ({ entry, onClose, onSaved }: { entry: ConfigKey; onClose: () => void; onSaved: () => void }) => {
    const { addNotification } = useNotification()
    const original = useMemo(() => pretty(entry.value), [entry.key, entry.updated_at])
    const [text, setText] = useState(original)
    /** Keys with a form of their own. Everything else is JSON, which is the
     *  honest default: a made-up form for a shape nobody has looked at is a
     *  worse lie than a textarea. */
    const isWindow = entry.key === GAME_WINDOW_KEY
    const isLatency = entry.key === LATENCY_KEY
    const formed = isWindow || isLatency
    const [form, setForm] = useState<GameWindowValue>(() => asWindows(entry.value))
    const [latency, setLatency] = useState<JoinLatencyValue>(() => asLatencyGate(entry.value))
    // An escape hatch, because a form can only offer what it was taught and
    // the registry may accept more.
    const [raw, setRaw] = useState(false)
    const [enabled, setEnabled] = useState(entry.exists ? entry.enabled : true)
    const [notes, setNotes] = useState(entry.notes)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [confirmDelete, setConfirmDelete] = useState(false)
    const [history, setHistory] = useState<ConfigChange[] | null>(null)

    useEffect(() => {
        let alive = true
        getHistory(entry.key)
            .then((h) => alive && setHistory(h.changes))
            .catch(() => alive && setHistory([]))
        return () => {
            alive = false
        }
    }, [entry.key])

    // Parsed here only to catch a typo before a round trip; the server is the
    // one that decides whether the shape is right.
    let parsed: unknown = undefined
    let syntax: string | null = null
    try {
        parsed = text.trim() === '' ? null : JSON.parse(text)
    } catch (e: any) {
        syntax = e.message
    }

    /** What this sheet would save, whichever editor is on. */
    const formValue = isLatency ? latency : form
    const asJson = formed && !raw ? JSON.stringify(formValue, null, 2) : text
    const dirty =
        asJson !== original || enabled !== (entry.exists ? entry.enabled : true) || notes !== entry.notes

    const save = async () => {
        setSaving(true)
        setError(null)
        try {
            const res = await saveKey(entry.key, {
                value: formed && !raw ? formValue : parsed,
                enabled,
                notes,
            })
            addNotification(
                res.unchanged
                    ? 'Nothing changed, so the fleet was not disturbed.'
                    : `${entry.key} saved as r${res.revision}`,
                'success'
            )
            onSaved()
        } catch (err: any) {
            setError(err.message)
        } finally {
            setSaving(false)
        }
    }

    const remove = async () => {
        setConfirmDelete(false)
        try {
            await deleteKey(entry.key)
            addNotification(`${entry.key} removed; the game falls back to its compiled value`, 'success')
            onSaved()
        } catch (err: any) {
            setError(err.message)
        }
    }

    return (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4' onClick={onClose}>
            <div
                onClick={(e) => e.stopPropagation()}
                className='flex max-h-[88vh] w-full max-w-3xl flex-col gap-3 overflow-auto rounded border border-white/20 bg-zinc-900 p-4 text-xs font-mono text-white'
            >
                <div className='flex flex-row items-baseline justify-between gap-3'>
                    <span className='flex min-w-0 flex-row items-baseline gap-2'>
                        <span className='truncate text-sm font-bold'>{entry.key}</span>
                        <span className='text-white/40'>{entry.audience_label}</span>
                    </span>
                    <button
                        type='button'
                        onClick={onClose}
                        className='shrink-0 text-white/40 underline hover:text-white'
                    >
                        close
                    </button>
                </div>

                <span className='text-white/50'>{entry.help}</span>

                {entry.deliverable_error && (
                    <div className='rounded border border-red-400/40 px-3 py-2 text-red-400'>
                        Stored, but not being sent: {entry.deliverable_error}
                    </div>
                )}

                <label className='flex cursor-pointer flex-row items-center gap-2'>
                    <input type='checkbox' checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
                    <span className='uppercase tracking-wide text-white/70'>Send this key</span>
                    <span className='text-white/30'>off means the game uses whatever is compiled into the build</span>
                </label>

                {formed && !raw ? (
                    isLatency ? (
                        <JoinLatencyGateForm value={latency} onChange={setLatency} />
                    ) : (
                        <GameWindowEditor value={form} onChange={setForm} />
                    )
                ) : (
                    <div className='flex flex-col gap-1'>
                        <span className='text-[10px] uppercase tracking-widest text-white/40'>Value (JSON)</span>
                        <textarea
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            spellCheck={false}
                            rows={16}
                            className={`w-full rounded border bg-black/40 p-2 font-mono text-xs outline-none ${
                                syntax ? 'border-red-400/60' : 'border-white/20 focus:border-white/40'
                            }`}
                        />
                        {syntax && <span className='text-red-400'>Not valid JSON: {syntax}</span>}
                    </div>
                )}

                {formed && (
                    <button
                        type='button'
                        onClick={() => {
                            // Carry the work across rather than dropping it.
                            if (raw) {
                                if (isLatency) setLatency(asLatencyGate(parsed))
                                else setForm(asWindows(parsed))
                            } else {
                                setText(JSON.stringify(formValue, null, 2))
                            }
                            setRaw((v) => !v)
                        }}
                        className='self-start text-[10px] uppercase tracking-widest text-white/40 underline hover:text-white'
                    >
                        {raw ? 'back to the form' : 'edit the JSON instead'}
                    </button>
                )}

                <label className='flex flex-col gap-1'>
                    <span className='text-[10px] uppercase tracking-widest text-white/40'>Notes</span>
                    <input
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder='why this is set the way it is'
                        className='rounded border border-white/20 bg-transparent px-2 py-1 outline-none focus:border-white/40'
                    />
                </label>

                {error && <div className='rounded border border-red-400/40 px-3 py-2 text-red-400'>{error}</div>}

                {confirmDelete && (
                    <ConfirmBar
                        message={`Remove ${entry.key}?`}
                        detail='The row goes and the game falls back to the value compiled into the build. The change is recorded either way.'
                        confirmLabel='Remove it'
                        onCancel={() => setConfirmDelete(false)}
                        onConfirm={remove}
                    />
                )}

                <div className='flex flex-row items-center justify-end gap-3'>
                    {entry.exists && (
                        <button
                            type='button'
                            onClick={() => setConfirmDelete(true)}
                            className='mr-auto text-[10px] uppercase tracking-widest text-red-400 underline'
                        >
                            remove
                        </button>
                    )}
                    {dirty && <span className='uppercase tracking-wide text-white/50'>unsaved</span>}
                    <button
                        type='button'
                        onClick={save}
                        disabled={
                            !dirty ||
                            saving ||
                            Boolean(formed && !raw ? null : syntax) ||
                            Boolean(isLatency && !raw && latencyGateProblem(latency))
                        }
                        className='rounded bg-violet-600/80 px-4 py-1 text-xs font-bold uppercase tracking-wide hover:bg-violet-600 disabled:opacity-30'
                    >
                        {saving ? 'saving…' : 'Save and send'}
                    </button>
                </div>

                {history && history.length > 0 && (
                    <div className='flex flex-col gap-1 border-t border-white/10 pt-3'>
                        <span className='text-[10px] uppercase tracking-widest text-white/40'>This key's history</span>
                        {history.map((c) => (
                            <div key={c.id} className='flex flex-row items-baseline gap-3'>
                                <span className='w-12 shrink-0 tabular-nums text-white/30'>r{c.revision}</span>
                                <span className='w-28 shrink-0 text-white/50'>{c.action_label}</span>
                                <span className='w-24 shrink-0 truncate text-white/40'>{c.by}</span>
                                <span className='shrink-0 text-white/30'>{ago(c.at)}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}

export default RemoteConfig
